import React, {
    MutableRefObject,
    ReactElement, ReactNode, useEffect, useMemo, useRef, useState,
} from "react";
import {
    GroupBase, NonceProvider, OnChangeValue, OptionsOrGroups, SelectInstance as BaseSelectType, ClassNamesConfig,
    CSSObjectWithLabel,
} from "react-select";
import ReactSelect from "react-select/async";
import classNames from "classnames";
import { Option, OptionWithChildren, SelectInstanceHandler } from "./Select";
import Styles from "./Select.module.scss";

export interface Props<T, TMulti extends boolean = false> {
    placeholder?: string;
    isMulti?: TMulti;
    value?: TMulti extends true ? Option<T>[] : Option<T>;
    onChange?: (options: (TMulti extends true ? readonly Option<T>[] : Option<T>)) => void;
    onBlur?: () => void;
    className?: string;
    isClearable?: boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
    minRequired?: number;
    dropdownIcon?: ReactNode;
    loadOptions: (initial: string) => Promise<Option<T>[] | OptionWithChildren<T>[]>

}

function getCount(value: unknown[] | unknown | undefined): number {
    if (Array.isArray(value)) {
        return value.length;
    }
    return value ? 1 : 0;
}

const AsyncSelect = React.forwardRef(<T, TMulti extends boolean = false>(
    props: Props<T, TMulti>,
    ref: React.ForwardedRef<SelectInstanceHandler>,
): ReactElement => {
    type Options = OptionsOrGroups<Option<T>, GroupBase<Option<T>>>;

    type SelectInstance = BaseSelectType<Option<T>, TMulti>;
    const [selectedCount, setSelectedCount] = useState(() => getCount(props.value));
    const innerRef = useRef<SelectInstance>();

    const loadOptions = (inputValue: string, callback: (options: Options) => void): void => {
        (async () => {
            try {
                const results = await props.loadOptions(inputValue);
                callback(results as Options);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Error loading options", err);
            }
        })();
    };

    useEffect(() => {
        setSelectedCount(getCount(props.value));
    }, [props.value]);

    useEffect(() => {
        const input = innerRef.current?.inputRef as unknown as HTMLInputElement;
        if (input) {
            input.required = selectedCount < (props.minRequired ?? 0);
        }
    }, [innerRef.current, selectedCount]);

    const onBlur = () => {
        const input = innerRef.current?.inputRef as unknown as HTMLInputElement;
        if (input) {
            input.required = selectedCount < (props.minRequired ?? 0);
        }
        props.onBlur?.();
    };

    const onChange = (val: OnChangeValue<Option<T>, TMulti>) => {
        props.onChange?.(val as (TMulti extends true ? readonly Option<T>[] : Option<T>));
        if (val) {
            setSelectedCount(Array.isArray(val) ? val.length : 1);
        } else {
            setSelectedCount(0);
        }
    };

    const setRef = (value: SelectInstance) => {
        if (ref) {
            // eslint-disable-next-line no-param-reassign
            (ref as MutableRefObject<SelectInstance>).current = value;
        }
        innerRef.current = value;
    };
    const classNamesConfig = {
        control: () => Styles["react-select-control"],
        container: () => Styles["react-select-container"],
        indicatorSeparator: () => Styles["react-select-separator"],
        indicatorsContainer: () => Styles["react-select-indicator"],
        singleValue: () => Styles["react-select-single-value"],
        multiValue: () => Styles["react-select-multi-value"],
        multiValueLabel: () => Styles["react-select-multi-label"],
        menuPortal: () => Styles["react-select-menu-portal"],
        input: () => Styles["react-select-input"],
        menu: () => Styles["react-select-menu"],
        placeholder: () => Styles["react-select-placeholder"],
        option: prop => classNames(
            Styles["react-select-menu-option"],
            prop.isFocused ? "hover" : undefined,
            prop.isSelected ? "active" : undefined,
        ),

    } as ClassNamesConfig<Option<T>>;

    const components = useMemo(() => (props.dropdownIcon ? ({
        DropdownIndicator: () => props.dropdownIcon,
    }) : undefined), [props.dropdownIcon]);

    return (
        <NonceProvider cacheKey="react-select" nonce={window.NONCE}>
            <ReactSelect
                ref={setRef}
                placeholder={props.placeholder}
                isMulti={props.isMulti}
                onChange={val => onChange(val as OnChangeValue<Option<T>, TMulti>)}
                onBlur={onBlur}
                classNames={classNamesConfig}
                isClearable={props.isClearable}
                value={props.value}
                menuPortalTarget={document.body}
                onKeyDown={props.onKeyDown}
                className={props.className}
                components={components}
                styles={{
                    menuPortal: base => ({
                        ...base,
                        zIndex: 1100,
                    } as CSSObjectWithLabel),
                }}
                cacheOptions
                loadOptions={loadOptions}
                defaultOptions
            />
        </NonceProvider>
    );
});

AsyncSelect.defaultProps = {
    placeholder: undefined,
    isMulti: false,
    onChange: undefined,
    onBlur: undefined,
    className: undefined,
    isClearable: false,
    onKeyDown: undefined,
    minRequired: undefined,
    value: undefined,
    dropdownIcon: undefined,
};

export default AsyncSelect as <
    T,
    TMulti extends boolean = false,
> (
    props: Props<T, TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof AsyncSelect>;
